// Tee sheet: every tee time, grouped by date. Read-only for players,
// editable (tee boxes) for admins.

import React, { useMemo, useState } from 'react';
import { Calendar, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import MatchCard from './MatchCard';
import { formatDate } from '../utils/helpers';
import { BRACKET_ROUND_LABELS } from '../data/seedSchedule';

export default function TeeSheet({
  matches, golfers, teeSets, courses, seasonDates, isAdmin, onChangeTee,
}) {
  const [collapsed, setCollapsed] = useState({});
  const [filter, setFilter] = useState('all');

  const grouped = useMemo(() => {
    const list = (matches || []).filter(m => {
      if (filter === 'regular') return (m.phase || 'regular') === 'regular';
      if (filter === 'playoff') return m.phase === 'playoff';
      return true;
    });
    const m = {};
    list.forEach(x => {
      m[x.match_date] = m[x.match_date] || [];
      m[x.match_date].push(x);
    });
    Object.values(m).forEach(l => l.sort((a, b) =>
      String(a.tee_time).localeCompare(String(b.tee_time)) ||
      String(a.course_id).localeCompare(String(b.course_id))));
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [matches, filter]);

  const dateLabel = (iso) =>
    (seasonDates || []).find(d => d.play_date === iso)?.label || '';

  if (!matches || matches.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        No matches yet. An admin can create them from the <strong>Schedule</strong> tab.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1">
        {[['all', 'All'], ['regular', 'Regular season'], ['playoff', 'Playoffs']].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium ${
              filter === v ? 'bg-gray-900 text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {l}
          </button>
        ))}
      </div>

      {grouped.map(([date, list]) => {
        const isCollapsed = collapsed[date];
        const byCourse = {};
        list.forEach(m => { byCourse[m.course_id] = (byCourse[m.course_id] || 0) + 1; });
        return (
          <div key={date} className="rounded-lg border border-gray-200">
            <button
              onClick={() => setCollapsed(c => ({ ...c, [date]: !c[date] }))}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
            >
              <span className="flex flex-wrap items-center gap-2">
                {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="font-semibold text-gray-900">{formatDate(date)}</span>
                <span className="text-sm text-gray-500">{dateLabel(date)}</span>
              </span>
              <span className="flex items-center gap-2 text-xs text-gray-500">
                {Object.entries(byCourse).map(([cid, n]) => {
                  const c = (courses || []).find(x => x.id === cid);
                  return (
                    <span key={cid} className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />{c?.short_name || cid} ({n})
                    </span>
                  );
                })}
              </span>
            </button>

            {!isCollapsed && (
              <div className="space-y-2 border-t border-gray-100 p-3">
                {list.map(m => (
                  <MatchCard
                    key={m.id}
                    match={m}
                    golfers={golfers}
                    teeSets={teeSets}
                    courses={courses}
                    isAdmin={isAdmin}
                    onChangeTee={onChangeTee}
                    headerExtra={m.phase === 'playoff'
                      ? <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800">
                          {BRACKET_ROUND_LABELS[m.bracket_round] || 'Playoff'}
                        </span>
                      : null}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
